import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  isSupportedLanguageObject,
  LanguageObject,
  codeToLanguageCodes,
} from "@faro-lotv/flat-ui";
import { useLocalize } from "@faro-lotv/foreign-observers";
import { useEffect, useState } from "react";
import { useSettingsApiClient } from "@api/use-settings-api-client";
import { SettingsApiUtils } from "@stellar/settings-api-client";

type OriginOfLanguageChange = "user menu" | "account settings";

interface ReturnProps {
  /** All the supported languages */
  supportedLanguages: LanguageObject[];

  /** Callback when the language is changed */
  onLanguageChange: (
    languageOrigin: OriginOfLanguageChange,
    languageCode: string
  ) => void;

  /** The current selected language */
  currentLanguage: LanguageObject;
}

/** A hook that handles all the utility functions for language selector */
export function useLanguage(): ReturnProps {
  const {
    availableLanguages,
    setLanguage,
    currentLanguage: LocalizeCurrentLanguage,
  } = useLocalize();
  const { trackEvent } = useTrackEvent();
  const settingsApiClient = useSettingsApiClient();

  // The default language of LocalizeJs is used as initial value as it considers browser language, query params etc.
  const [updatedModifiedAt, setUpdatedModifiedAt] = useState<string>("0");

  // Fetch the current language. If the language is not set by the user yet, the default language of LocalizeJs is used.
  // If the user has set a language, it will be used instead.
  useEffect(() => {
    async function fetchUserSettings(): Promise<void> {
      const { value, modifiedAt, modifiedBy } =
        await settingsApiClient.user.fetchUserSettings("language");
      const languageObject = availableLanguages.find(
        ({ code }) => code === value
      );

      if (languageObject && !SettingsApiUtils.isDefaultSettings(modifiedBy)) {
        setUpdatedModifiedAt(modifiedAt);
        setLanguage(languageObject.code);
      }
    }
    void fetchUserSettings();
  }, [availableLanguages, settingsApiClient.user, setLanguage]);

  async function onLanguageChange(
    languageOrigin: OriginOfLanguageChange,
    languageCode: string
  ): Promise<void> {
    const languageObject = availableLanguages.find(
      ({ code }) => code === languageCode
    );

    if (languageObject) {
      trackEvent({
        name: UserProfile.changeLanguage,
        props: { language: languageObject.code, origin: languageOrigin },
      });
      setLanguage(languageObject.code);
      const { modifiedAt } = await settingsApiClient.user.updateUserSettings(
        "language",
        {
          value: codeToLanguageCodes(languageObject.code),
          modifiedAt: updatedModifiedAt,
        }
      );
      setUpdatedModifiedAt(modifiedAt);
    }
  }

  return {
    supportedLanguages: availableLanguages.filter(isSupportedLanguageObject),
    onLanguageChange,
    currentLanguage: {
      code: codeToLanguageCodes(LocalizeCurrentLanguage.code),
      name: LocalizeCurrentLanguage.name,
    },
  };
}
